import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  DataError,
  LoadingIndicator,
  useAlert
} from "@digital-services/ui";

import { useAssociateAdmin } from "@organization/services/use-associate-admin";
import { useGetOrganization } from "@organization/services/use-get-organization";
import { DisplayNameAdvancedField } from "@user/components/DisplayNameAdvancedField";
import { EmailAdvancedField } from "@user/components/EmailAdvancedField";
import { useInviteFormContext } from "@user/context";

interface Props {
  organizationId: string;
  onCancel: () => void;
  onSave: () => void;
}
export const InviteAdminForm: FunctionComponent<Props> = props => {
  const { organizationId, onCancel, onSave } = props;
  const { inviteInfoObj } = useInviteFormContext();

  const { organization, isLoading, error } = useGetOrganization(organizationId);

  const [email, setEmail] = useState<string>("");
  const [displayName, setDisplayName] = useState("");

  const [isBusy, setIsBusy] = useState(false);
  const [reInviteMode, setReInviteMode] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(true);

  const { t } = useTranslation();

  const { t: tOrg } = useTranslation(undefined, {
    keyPrefix: "organization"
  });
  const { t: tAdmins } = useTranslation(undefined, {
    keyPrefix: "organization.admins"
  });
  const { t: tAdmin } = useTranslation(undefined, {
    keyPrefix: "organization.admin"
  });

  const { associate } = useAssociateAdmin();
  const { notifySuccess, notifyError } = useAlert();

  useEffect(() => {
    if (inviteInfoObj.displayName) {
      setDisplayName(inviteInfoObj.displayName);
    } else {
      // if NO fresh value from server then need to clear local value
      setDisplayName("");
      setDisableSubmit(true);
    }

    if (inviteInfoObj.reInviteMode !== undefined) {
      setReInviteMode(inviteInfoObj.reInviteMode);
    }

    if (inviteInfoObj.disableSubmit !== undefined) {
      setDisableSubmit(inviteInfoObj.disableSubmit);
    }
  }, [inviteInfoObj]);

  function handleDisplayNameChange(newValue: string): void {
    setDisplayName(newValue);
  }

  function handleEmailChange(newValue: string): void {
    setEmail(newValue);
  }

  function saveChanges(): void {
    setIsBusy(true);

    const dataToSave = {
      emailAddress: email,
      displayName: displayName
    };

    associate(dataToSave, organizationId)
      .then(response => {
        setIsBusy(false);

        // Backend does have validation 409 error here
        // Then response.message here = "Failed to add user with email '***', active invitation already exists"
        // But Frontend should NOT allow it
        // Besides, it might be other errors here, then toast will be shown

        if (response instanceof Error) {
          notifyError({
            header: tAdmins("invite.error.message", {
              organization: organization?.name,
              user: displayName
            }),
            "data-testid": "associate-admin-error-alert-1"
          });
        } else {
          onSave();
          notifySuccess({
            header: tAdmins("invite.success.message", {
              organization: organization?.name,
              user: displayName
            }),
            "data-testid": "associate-admin-success-alert"
          });
        }
      })
      .catch(() => {
        // Errors from above then() are NOT re-thrown from postData)() call, but caught inside.
        // As result it doesn't bubble up to here, and this code seems to be dead.
        setIsBusy(false);
        notifyError({
          header: tAdmins("invite.error.message", {
            organization: organization?.name,
            user: displayName
          }),
          "data-testid": "associate-admin-error-alert-2"
        });
      });
  }

  if (isLoading) {
    return (
      <div className="flex w-full items-center text-base">
        <LoadingIndicator label={tOrg("load.message")} />
      </div>
    );
  }

  if (error || !organization) {
    return (
      <DataError
        title={tOrg("load.error.message")}
        message=""
        className="text-left! text-red-600"
      />
    );
  }

  return (
    <div className="flex flex-col gap-10" data-testid="invite-admin-form">
      <EmailAdvancedField
        label={tAdmin("email.label")}
        value={email}
        placeholder={tAdmin("email.placeholder")}
        onInputChange={handleEmailChange}
      />

      <DisplayNameAdvancedField
        label={tAdmin("display-name.label")}
        value={displayName}
        placeholder={tAdmin("display-name.placeholder")}
        onInputChange={handleDisplayNameChange}
      />

      <div className="flex flex-row-reverse gap-4">
        <Button
          className="select-none"
          data-testid="save"
          variant="primary"
          isLoading={isBusy}
          onClick={saveChanges}
          disabled={disableSubmit}
        >
          {reInviteMode
            ? t("button.re-invite.label")
            : t("button.invite.label")}
        </Button>
        <Button className="select-none" data-testid="cancel" onClick={onCancel}>
          {t("button.cancel.label")}
        </Button>
      </div>
    </div>
  );
};
